<template>
  <div class="rtb-stats__widget">
    <template v-if="hasRtb">
      <SmartTableWidget
        :widget="{
          tableTitle: $t('RTB_BREAKDOWN_PANE_TITLE'),
          iconClass: 'fa fa-trophy',
          locale: $options.i18n
        }"
        :date-range="publisherRange"
        :column-defs="columnSettings.tableData"
        :row-data="tableData || []"
        :enable-export="true"
        :export-title="formatTitleWithCampaignName('RTB Stats by Publisher')"
        icon="fa-chevron-down"
        @update-date-range="updateStats"
      >
        <template #table>
          <LoadingStatusDisplay
            v-if="loadingBreakdown"
            :loading="true"
          />

          <div
            v-else-if="!tableData.length"
            class="empty-records"
          >
            {{ $t('RTB_BREAKDOWN_PANE_EMPTY') }}
          </div>
        </template>
      </SmartTableWidget>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import SmartTableWidget from '@/core/components/SmartTable/SmartTableWidget.vue';
import {
  GET_RTB_STATS,
  GET_CAMPAIGN_RTB_STATS,
  GET_CAMPAIGN_RTB_STATS_FOR_PUBLISHER
} from '@/modules/rtbStats/store/actionTypes';
import { toRequestParams } from '@/modules/rtbStats/store/utils';
import LoadingStatusDisplay from '@/core/components/Loader/LoadingStatusDisplay.vue';
import { STATUS_PENDING, STATUS_SUCCESS } from '@/store/helpers';
import cloneDeep from 'lodash/cloneDeep';

/**
 * @type {Record<"errorsByPublisher" | "tableData",
 * [import('@ag-grid-community/core').ColDef
 *  & {field: (data.structures.RtbBreakdownByPublisherTableKeys | data.structures.RtbErrorTableKeys) }]>}
 */
import { columnSettings } from '../columnSettings';

export default {
  components: { SmartTableWidget, LoadingStatusDisplay },
  props: {
    campaign: {
      type: Object,
      default: () => {
        return {};
      }
    },
    isAffiliate: {
      type: Boolean,
      default: null
    }
  },
  data: () => ({
    publisherRange: 'Today',
    columnSettings
  }),
  computed: {
    ...mapGetters('rtbStats', [
      'statusSummary',
    ]),

    ...mapState('rtbStats', [
      'affiliatesStats',
    ]),

    ...mapState({
      flags: 'currentAccountFeatureFlags'
    }),
    baseStartRequestBody() {
      return { past: 0, days: 1, campaignId: this.campaign.id };
    },
    loaded() {
      return this.statusSummary.GET_RTB_STATS == STATUS_SUCCESS;
    },
    loadingBreakdown() {
      return this.statusSummary.GET_RTB_STATS === STATUS_PENDING;
    },

    errors() {
      return {
        breakdown: this.$store.state.rtbStats.status?.GET_RTB_STATS_Status
          ?.error
      };
    },

    /** @type {() => *[]} */
    tableData() {
      if (!this.loaded || !this.affiliatesStats?.length)
        return [];

      // Create method to do that here and in errorsByPublisher
      return Array.from(cloneDeep(this.affiliatesStats), this.toTableFormat);
    },

    /**
     *  Parameters based on user settings.
     *  This is watched, once changed will trigger the action from the store
     *  @type {() => data.structures.RtbStatsRequestParams}
     */
    requestParamsSales() {
      return toRequestParams(this.publisherRange);
    },
    /**
     * Checks if the user has rtb enabled
     * @type {() => boolean}
     */
    hasRtb() {
      return !!this.flags.rtb;
    }
  },
  /**
   * When mounted, pull the data needed
   */
  mounted() {
    if(this.isAffiliate) {
      this.getCampaignStatsForPublisher(this.baseStartRequestBody);
    }
    else if(!this.campaign.id) {
      this.getStats({ past: 0, days: 1 });
    } else {      
      this.getCampaignStats(this.baseStartRequestBody);
    }
  },
  methods: {
    ...mapActions('rtbStats', {
      getStats: GET_RTB_STATS,
      getCampaignStats: GET_CAMPAIGN_RTB_STATS,
      getCampaignStatsForPublisher: GET_CAMPAIGN_RTB_STATS_FOR_PUBLISHER
    }),

    updateStats(label) {
      const params = toRequestParams(label);
      if(this.isAffiliate) {
        this.getCampaignStatsForPublisher({ past: params.past, days: params.days, campaignId: this.campaign.id });
      }
      else if(!this.campaign.id) {
        this.getStats(params);
      } else {
        this.getCampaignStats({ past: params.past, days: params.days, campaignId: this.campaign.id });
      }
    },
    toTableFormat(publisher) {
      const { id, name, values }=publisher;
      if(this.campaign.id) {
        // Formatting it here makes possible to export in the right format already
        values.rtbwinningbidpercent = values.rtbwinningbidpercent*100;
        values.rtbacceptedwinningbidpercent = values.rtbacceptedwinningbidpercent*100;
      }
      return {
        affiliateId: id,
        affiliateName: name,
        ...values
      };
    },
    formatTitleWithCampaignName(baseTitle) {
      let increment = '';
      if(this.campaign.name) {
        increment = ' - ' + this.campaign.name;
      }
      return baseTitle + increment;
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style lang="scss" scoped>

</style>
